import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { selectAuth } from "src/Redux/Slices/auth";
import { PATH_LOGIN } from "src/Routes";
import styled from "styled-components";
import {
    Alert,
    Button,
    Box,
    Typography,
    TextField,
    MenuItem,
    SwipeableDrawer,
    Fab,
    IconButton,
    useMediaQuery,
    Dialog,
    DialogTitle,
    List,
    ListItemButton,
    ListItemText,
    Snackbar,
    CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import moment from "moment";
import {
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";

import { useStore } from "react-redux";

import { useAppDispatch, useAppSelector } from "src/Redux/store";
import {
    selectTripPlanner,
    setTripMeta,
    addItem,
    moveItem,
    removeItem,
    resizeItem,
    checkTripItemAvailability,
    loadTripFromQuote,
    extendTripToInclude,
} from "src/Redux/Slices/tripPlanner";
import { ITripItem } from "@interfaces";
import { LoadingStage } from "src/Interfaces/shared";
import { AppModuleLayout } from "@layouts";
import { ProductSidebar } from "./components/ProductSidebar";
import { CalendarGrid } from "./components/CalendarGrid";
import { DraggableProduct } from "./components/ProductCard";
import { AvailabilityModal } from "./components/AvailabilityModal";
import { TripSummary } from "./components/TripSummary";
import { TripItemEditModal } from "./components/TripItemEditModal";
import { TravellerPickerModal } from "./components/TravellerPickerModal";
import { RosterModal } from "./components/RosterModal";

const PageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    padding: 16px 24px;
    gap: 16px;

    @media (max-width: 899px) {
        padding: 12px;
        /* Leave room for the floating "Add products" FAB so the bottom of
           the page (e.g. the trip summary "Add to cart" button) doesn't
           sit underneath it. */
        padding-bottom: 96px;
    }
`;

const TopBar = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px;
    padding: 12px 16px;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;

    @media (max-width: 899px) {
        gap: 8px;
        padding: 10px 12px;
    }
`;

const Layout = styled.div`
    display: grid;
    grid-template-columns: 320px 1fr;
    gap: 16px;
    flex: 1;
    min-height: 0;

    @media (max-width: 899px) {
        grid-template-columns: 1fr;
    }
`;

const Panel = styled.div`
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 16px;
    min-height: 400px;
    display: flex;
    flex-direction: column;
    min-width: 0;

    @media (max-width: 899px) {
        padding: 12px;
        min-height: 0;
    }
`;

const SidebarPanel = styled(Panel)`
    @media (max-width: 899px) {
        display: none;
    }
`;

const FabBar = styled.div`
    display: none;
    @media (max-width: 899px) {
        display: block;
        position: fixed;
        right: 16px;
        bottom: 16px;
        z-index: 1200;
    }
`;

const DrawerHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px 0 16px;
`;

const DrawerBody = styled.div`
    padding: 8px 16px 16px;
    /* Bottom-sheet body — leave roughly half the viewport visible for the
       calendar so the user can drag from a product card down to a day cell. */
    height: 50vh;
    display: flex;
    flex-direction: column;
    min-height: 0;
`;

// Auto-trigger availability check for any item flagged unknown. The key
// includes `availabilityVersion` so a re-check fires whenever the item is
// moved/resized/quantity-changed (which bumps the version).
const useUnknownItemAvailabilityChecks = (items: ITripItem[]) => {
    const dispatch = useAppDispatch();
    const checkedKeysRef = useRef<Set<string>>(new Set());
    useEffect(() => {
        const aliveKeys = new Set<string>();
        for (const it of items) {
            const key = `${it.id}@v${it.availabilityVersion}`;
            aliveKeys.add(key);
            if (it.availabilityStatus === "unknown" && !checkedKeysRef.current.has(key)) {
                checkedKeysRef.current.add(key);
                dispatch(checkTripItemAvailability({ itemId: it.id }));
            }
        }
        // Drop bookkeeping for items/versions that no longer exist so memory
        // doesn't grow unbounded across many edits.
        Array.from(checkedKeysRef.current).forEach((k) => {
            if (!aliveKeys.has(k)) checkedKeysRef.current.delete(k);
        });
    }, [items, dispatch]);
};

export const TripPlanner = () => {
    const dispatch = useAppDispatch();
    const { current, loadStage, lastError } = useAppSelector(selectTripPlanner);
    const { tripId } = useParams<{ tripId?: string }>();

    // We need the *id* of the item that `addItem` just created so the
    // confirmation snackbar's "Undo" button can target it. The reducer
    // generates the id internally and doesn't return it, so we read the
    // freshly-pushed item off the store right after dispatch via useStore.
    const store = useStore();
    const readLastItemId = useCallback((): string | undefined => {
        const state = store.getState() as {
            tripPlanner: { current: { items: ITripItem[] } };
        };
        const items = state.tripPlanner.current.items;
        return items[items.length - 1]?.id;
    }, [store]);

    // Confirmation toast shown after a product is added to a day (via either
    // the mobile tap-to-add picker or a desktop drag-drop). Auto-dismisses
    // after ~2s; the "Undo" action removes the just-added item.
    const [addedToast, setAddedToast] = useState<{
        itemId: string;
        productName: string;
        dayIndex: number;
    } | null>(null);
    const showAddedToast = useCallback(
        (productName: string, dayIndex: number) => {
            const itemId = readLastItemId();
            if (!itemId) return;
            setAddedToast({ itemId, productName, dayIndex });
        },
        [readLastItemId],
    );

    // When the route includes a `:tripId`, hydrate the planner from the saved
    // quote (unless the same trip is already loaded — avoids clobbering
    // unsaved local edits if the user navigates back to the same URL).
    useEffect(() => {
        if (!tripId) return;
        if (String(current.id) === String(tripId)) return;
        dispatch(loadTripFromQuote({ quoteId: tripId }));
    }, [tripId, current.id, dispatch]);

    // While a saved trip is being hydrated (route has a `:tripId` but the
    // loaded trip's id hasn't caught up yet) we must NOT render the planner —
    // otherwise the user stares at the previous/empty trip and assumes the
    // open "didn't reload". Surface an explicit loading state, and on failure
    // a clear error with a Retry instead of silently swallowing the rejection.
    const needsHydration = Boolean(tripId) && String(current.id) !== String(tripId);
    const hydrationFailed = needsHydration && loadStage === LoadingStage.LOADING_FAILURE;
    const hydrationInProgress = needsHydration && !hydrationFailed;
    const retryHydration = useCallback(() => {
        if (tripId) dispatch(loadTripFromQuote({ quoteId: tripId }));
    }, [dispatch, tripId]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
        useSensor(KeyboardSensor),
    );

    const handleDragEnd = useCallback(
        (e: DragEndEvent) => {
            const { active, over } = e;
            if (!over) return;
            // Each draggable / droppable in this page registers a `data`
            // payload with one of these three discriminated shapes (see
            // ProductCard / TripItemCard / CalendarGrid). Modeling them
            // here lets us avoid an `any` escape on `data.current`.
            type DropData = { kind: "day"; dayIndex: number };
            type DragData =
                | { kind: "product"; product: DraggableProduct }
                | { kind: "item"; itemId: string };
            const overData = over.data.current as DropData | undefined;
            if (overData?.kind !== "day") return;
            const { dayIndex } = overData;

            const activeData = active.data.current as DragData | undefined;
            if (activeData?.kind === "product") {
                const { product } = activeData;
                // Accommodation can't be priced by the quick-add path: its room
                // rate only resolves through the availability panel. Open the
                // room picker focused on the dropped day so the user selects a
                // priced room — otherwise the cart line would land at $0.
                if (product.productClass === "accommodation") {
                    const dropDate = moment(current.startDate, "YYYY-MM-DD")
                        .add(dayIndex, "days")
                        .format("YYYY-MM-DD");
                    setAccomPicker({ product, date: dropDate });
                    return;
                }
                // The product's full calendar footprint (e.g. 2 for a 1-night
                // hotel). Grow the trip so the WHOLE span fits before adding —
                // otherwise dropping near the trip end makes addItem's clamp
                // (maxSpan = durationDays - dayIndex) collapse a multi-day item
                // to a single day, so accommodation renders as an in-cell card
                // instead of a spanning band. Mirrors AvailabilityModal's
                // "Add to Trip" path so both add methods behave identically.
                const span = product.durationDays || 1;
                if (span > 1) {
                    const endIso = moment(current.startDate, "YYYY-MM-DD")
                        .add(dayIndex + span - 1, "days")
                        .format("YYYY-MM-DD");
                    dispatch(extendTripToInclude({ date: endIso }));
                }
                dispatch(
                    addItem({
                        productId: product.productId,
                        tdmsProductId: product.tdmsProductId,
                        name: product.name,
                        productImagePath: product.productImagePath,
                        productClass: product.productClass,
                        fareTypeId: product.fareTypeId,
                        productPricesDetailsId: product.productPricesDetailsId,
                        // Live-rate booking keys (accommodation only) — carried
                        // through, not displayed, so the room can be booked at
                        // checkout. Absent on CMS/generic & non-accommodation.
                        defaultRateCode: product.defaultRateCode,
                        roomCode: product.roomCode,
                        externalId: product.externalId,
                        externalSource: product.externalSource,
                        rrp: product.rrp,
                        numPax: product.numPax,
                        durationDays: span,
                        dayIndex,
                    }),
                );
                showAddedToast(product.name, dayIndex);
                // The new item starts in `unknown` status, so the effect below will pick it up.
            } else if (activeData?.kind === "item") {
                const { itemId } = activeData;
                dispatch(moveItem({ itemId, toDayIndex: dayIndex }));
                dispatch(checkTripItemAvailability({ itemId }));
            }
        },
        [dispatch, showAddedToast, current.startDate],
    );

    useUnknownItemAvailabilityChecks(current.items);

    // Item-edit modal: opened by clicking any TripItemCard / SpanBand on the
    // calendar. Holds the id (not the item itself) so the modal always reads
    // the latest item state from Redux after edits.
    const [editingItemId, setEditingItemId] = useState<string | null>(null);
    const editingItem = editingItemId
        ? current.items.find((i) => i.id === editingItemId) ?? null
        : null;

    // Per-product traveller picker: opened from the edit modal's "Travellers"
    // button. Keyed by item id so it always reads the latest item from Redux.
    const [travellersItemId, setTravellersItemId] = useState<string | null>(null);
    const travellersItem = travellersItemId
        ? current.items.find((i) => i.id === travellersItemId) ?? null
        : null;

    // Trip-level roster editor: opened from the "Travellers" button in the
    // header (and from the picker when the roster is empty/insufficient).
    const [rosterOpen, setRosterOpen] = useState(false);

    // Bottom-sheet sidebar (mobile only). Below the md breakpoint the
    // sidebar collapses behind a FAB that opens a half-height drawer; the
    // calendar remains visible above so a touch-drag can leave the drawer
    // and land on a day cell.
    const isMobile = useMediaQuery("(max-width: 899px)");
    const [sidebarOpen, setSidebarOpen] = useState(false);

    // Tap-to-add fallback for mobile users who struggle with the
    // long-press-then-drag gesture: tapping a ProductCard inside the bottom
    // sheet stages the product here and opens a small "Add to which day?"
    // picker. Selecting a day dispatches the same `addItem` action used by
    // the drag flow, so availability checks fire identically.
    const [pickingProduct, setPickingProduct] = useState<DraggableProduct | null>(null);
    const handleSelectProduct = useCallback((product: DraggableProduct) => {
        setPickingProduct(product);
    }, []);
    // Accommodation room picker. Unlike tours/activities, an accommodation's
    // room rate only resolves through the availability panel — the lightweight
    // quick-add carries no price, so a dropped/tapped hotel would land in the
    // cart at $0. Instead we stage the product + the chosen day's date here and
    // open the AvailabilityModal seeded to that date so the user picks a priced
    // room before it enters the trip.
    const [accomPicker, setAccomPicker] = useState<{
        product: DraggableProduct;
        date: string;
    } | null>(null);
    const handlePickDay = useCallback(
        (dayIndex: number) => {
            if (!pickingProduct) return;
            const product = pickingProduct;
            // Same as the drag path: accommodation must be priced through the
            // room picker, so route it there instead of the quick-add (which
            // would add it at $0).
            if (product.productClass === "accommodation") {
                const dropDate = moment(current.startDate, "YYYY-MM-DD")
                    .add(dayIndex, "days")
                    .format("YYYY-MM-DD");
                setAccomPicker({ product, date: dropDate });
                setPickingProduct(null);
                setSidebarOpen(false);
                return;
            }
            dispatch(
                addItem({
                    productId: product.productId,
                    tdmsProductId: product.tdmsProductId,
                    name: product.name,
                    productImagePath: product.productImagePath,
                    productClass: product.productClass,
                    fareTypeId: product.fareTypeId,
                    productPricesDetailsId: product.productPricesDetailsId,
                    // Live-rate booking keys (accommodation only) — carried
                    // through, not displayed, so the room can be booked at
                    // checkout. Absent on CMS/generic & non-accommodation.
                    defaultRateCode: product.defaultRateCode,
                    roomCode: product.roomCode,
                    externalId: product.externalId,
                    externalSource: product.externalSource,
                    rrp: product.rrp,
                    numPax: product.numPax,
                    durationDays: product.durationDays || 1,
                    dayIndex,
                }),
            );
            showAddedToast(product.name, dayIndex);
            setPickingProduct(null);
            setSidebarOpen(false);
        },
        [dispatch, pickingProduct, showAddedToast, current.startDate],
    );
    // After a drop completes, close the sheet so the user can immediately see
    // their new item. We don't close on drag-start because that would unmount
    // the dragged source mid-gesture and abort the drop.
    const handleDragEndWithClose = useCallback(
        (e: DragEndEvent) => {
            handleDragEnd(e);
            if (isMobile) setSidebarOpen(false);
        },
        [handleDragEnd, isMobile],
    );

    const { jwt } = useAppSelector(selectAuth);
    const isSignedIn = jwt !== null && jwt !== undefined;

    const content = (
        <PageWrapper>
            {!isSignedIn && (
                <Alert
                    severity="info"
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            component={RouterLink}
                            to={`${PATH_LOGIN}?redirect=${encodeURIComponent(
                                window.location.pathname + window.location.search,
                            )}`}
                            sx={{ fontWeight: 600 }}
                        >
                            Log in
                        </Button>
                    }
                    sx={{ borderRadius: 2 }}
                >
                    You need to be logged in to save trips or add them to your cart.
                </Alert>
            )}
            <TopBar>
                <Typography variant="h5" sx={{ mr: 2 }}>Trip Planner</Typography>
                <TextField
                    label="Trip name"
                    size="small"
                    value={current.name}
                    onChange={(e) => dispatch(setTripMeta({ name: e.target.value }))}
                />
                <TextField
                    label="Start date"
                    type="date"
                    size="small"
                    value={current.startDate}
                    onChange={(e) => dispatch(setTripMeta({ startDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    label="Days"
                    select
                    size="small"
                    value={current.durationDays}
                    onChange={(e) =>
                        dispatch(setTripMeta({ durationDays: Number(e.target.value) }))
                    }
                    sx={{ minWidth: 100 }}
                >
                    {Array.from({ length: 40 }, (_, i) => i + 1).map((d) => (
                        <MenuItem key={d} value={d}>{d}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    label="Travellers"
                    type="number"
                    size="small"
                    value={current.numTravellers}
                    onChange={(e) => {
                        const n = Math.max(1, Math.min(99, Number(e.target.value) || 1));
                        dispatch(setTripMeta({ numTravellers: n }));
                    }}
                    inputProps={{ min: 1, max: 99, step: 1 }}
                    title="Default quantity for new items"
                    sx={{ width: 90 }}
                />
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PeopleOutlineIcon />}
                    onClick={() => setRosterOpen(true)}
                >
                    Travellers
                    {current.travellers.length ? ` (${current.travellers.length})` : ""}
                </Button>
                <Box sx={{ flex: 1 }} />
                <Typography variant="body2" color="text.secondary">
                    {current.items.length} item{current.items.length === 1 ? "" : "s"}
                </Typography>
            </TopBar>

            <DndContext sensors={sensors} onDragEnd={handleDragEndWithClose}>
                <Layout>
                    {/* Conditionally render — not just CSS-hide — so the
                        bottom-sheet ProductSidebar is the only mounted
                        instance on mobile. Two simultaneous instances
                        could otherwise share draggable ids if the same
                        search results were loaded in both. */}
                    {!isMobile && (
                        <SidebarPanel>
                            <ProductSidebar />
                        </SidebarPanel>
                    )}
                    <Panel>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                            {current.name} — {current.durationDays} day
                            {current.durationDays === 1 ? "" : "s"}
                        </Typography>
                        <CalendarGrid
                            trip={current}
                            onRemoveItem={(id) => dispatch(removeItem({ itemId: id }))}
                            onResizeItem={(id, durationDays) =>
                                dispatch(resizeItem({ itemId: id, durationDays }))
                            }
                            onItemClick={(id) => setEditingItemId(id)}
                        />
                        <TripSummary />
                    </Panel>
                </Layout>

                {/* Mobile bottom-sheet sidebar. Lives inside DndContext so a
                    product card inside the drawer can be picked up and
                    dragged onto a day cell on the calendar above. */}
                <SwipeableDrawer
                    anchor="bottom"
                    open={isMobile && sidebarOpen}
                    onOpen={() => setSidebarOpen(true)}
                    onClose={() => setSidebarOpen(false)}
                    disableSwipeToOpen
                    ModalProps={{ keepMounted: true }}
                    PaperProps={{
                        sx: {
                            borderTopLeftRadius: 16,
                            borderTopRightRadius: 16,
                            // Keep the modal backdrop transparent so the
                            // calendar remains visible / draggable to.
                            backgroundImage: "none",
                        },
                    }}
                    slotProps={{ backdrop: { invisible: true } }}
                >
                    <DrawerHeader>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Add products
                        </Typography>
                        <IconButton
                            aria-label="Close add products"
                            onClick={() => setSidebarOpen(false)}
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>
                    </DrawerHeader>
                    <DrawerBody>
                        <ProductSidebar onSelectProduct={handleSelectProduct} />
                    </DrawerBody>
                </SwipeableDrawer>
            </DndContext>

            {/* Tap-to-add day picker. Shown only after the user taps a product
                inside the mobile bottom sheet. Lists every day in the trip
                with its real date so the choice is unambiguous. */}
            <Dialog
                open={pickingProduct !== null}
                onClose={() => setPickingProduct(null)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>
                    {pickingProduct ? `Add "${pickingProduct.name}" to…` : "Add to which day?"}
                </DialogTitle>
                <List dense sx={{ pt: 0 }}>
                    {Array.from({ length: current.durationDays }, (_, i) => i).map((dayIndex) => {
                        const date = moment(current.startDate, "YYYY-MM-DD").add(dayIndex, "days");
                        return (
                            <ListItemButton
                                key={dayIndex}
                                onClick={() => handlePickDay(dayIndex)}
                            >
                                <ListItemText
                                    primary={`Day ${dayIndex + 1}`}
                                    secondary={date.format("ddd, MMM D")}
                                />
                            </ListItemButton>
                        );
                    })}
                </List>
            </Dialog>

            <FabBar>
                <Fab
                    color="primary"
                    aria-label="Add products"
                    onClick={() => setSidebarOpen((v) => !v)}
                    variant="extended"
                >
                    <AddIcon sx={{ mr: 1 }} />
                    {sidebarOpen ? "Hide" : "Add products"}
                </Fab>
            </FabBar>

            <Snackbar
                open={addedToast !== null}
                autoHideDuration={2000}
                onClose={() => setAddedToast(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                message={
                    addedToast
                        ? `Added ${addedToast.productName} to Day ${addedToast.dayIndex + 1}`
                        : ""
                }
                action={
                    addedToast ? (
                        <Button
                            color="secondary"
                            size="small"
                            onClick={() => {
                                dispatch(removeItem({ itemId: addedToast.itemId }));
                                setAddedToast(null);
                            }}
                        >
                            Undo
                        </Button>
                    ) : null
                }
            />

            <TripItemEditModal
                open={editingItem !== null}
                item={editingItem}
                tripStartDate={current.startDate}
                tripDurationDays={current.durationDays}
                onClose={() => setEditingItemId(null)}
                onEditTravellers={(id) => {
                    setEditingItemId(null);
                    setTravellersItemId(id);
                }}
            />

            <TravellerPickerModal
                open={travellersItem !== null}
                item={travellersItem}
                roster={current.travellers}
                onClose={() => setTravellersItemId(null)}
                onManageRoster={() => {
                    setTravellersItemId(null);
                    setRosterOpen(true);
                }}
            />

            <RosterModal
                open={rosterOpen}
                travellers={current.travellers}
                numTravellers={current.numTravellers}
                canUseSavedTravellers={Boolean(jwt)}
                onClose={() => setRosterOpen(false)}
            />

            {/* Accommodation room picker. Opened when a hotel is dropped onto
                (or tapped into) a day. Seeded to that day's date so the user
                sees the room rates for the dropped date and picks a priced
                room — the modal's "Add to Trip" dispatches addItem with the
                resolved rrp + availability index. `key` forces a remount per
                product so the modal reloads the right product's availability. */}
            {accomPicker && (
                <AvailabilityModal
                    key={accomPicker.product.productId}
                    open
                    productId={accomPicker.product.productId}
                    productName={accomPicker.product.name}
                    initialDate={accomPicker.date}
                    productClassOverride={accomPicker.product.productClass}
                    onClose={() => setAccomPicker(null)}
                />
            )}
        </PageWrapper>
    );

    // Wrap in the shared app shell so the user always has the global header
    // (logo, account/menu, cart, location picker) and footer available while
    // planning. Previously the planner rendered bare — users had no way to
    // reach My Trips, My Account, or other top-level features from here.
    return (
        <AppModuleLayout>
            {hydrationInProgress ? (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        minHeight: "40vh",
                        p: 4,
                    }}
                >
                    <CircularProgress />
                    <Typography color="text.secondary">Loading your trip…</Typography>
                </Box>
            ) : hydrationFailed ? (
                <Box sx={{ p: 4, maxWidth: 560, mx: "auto" }}>
                    <Alert
                        severity="error"
                        action={
                            <Button color="inherit" size="small" onClick={retryHydration}>
                                Retry
                            </Button>
                        }
                        sx={{ borderRadius: 2 }}
                    >
                        {lastError || "We couldn't load this trip. Please try again."}
                    </Alert>
                </Box>
            ) : (
                content
            )}
        </AppModuleLayout>
    );
};

export default TripPlanner;
export { MyTrips } from "./MyTrips";
